/**
 * Wie gross ein Bild gedruckt werden darf - und welches Bild sich als Titelbild
 * eignet.
 *
 * Die Rechnung ist immer dieselbe: Druckgrösse ist Pixel geteilt durch dpi.
 * Ein Bild mit 1600 Pixeln längster Kante trägt bei echten 300 dpi 135 mm,
 * eines mit 2400 Pixeln gut 200 mm. Wird es grösser gesetzt, sinkt die
 * Auflösung unter 300 dpi und der Druck sieht matschig aus - auf dem Bildschirm
 * merkt man davon nichts, deshalb steht die Rechnung hier und nicht im Kopf des
 * Layouters.
 *
 * PhotoMeta kennt nur die Dateigrösse in Bytes, keine Pixelmasse. Sie beim
 * Laden zu ermitteln wäre möglich (createImageBitmap je Foto), kostet aber bei
 * über hundert Bildern eine zweite vollständige Dekodierrunde, nur um eine
 * Zahl zu erfahren, die am Ergebnis nichts ändert: die Rechnung unten zeigt,
 * dass auf A5 selbst die alten 1600-Pixel-Bilder jeden Platz dieses Layouts
 * tragen. Deshalb wird konservativ mit ANGENOMMENE_KANTE_PX gerechnet. Die
 * Prüfung bleibt trotzdem im Code: wer das Format ändert oder einen breiteren
 * Platz einführt, soll den Fehler von der Funktion und nicht vom Papier
 * erfahren.
 */

import type { PhotoMeta } from '../../lib/types'

/** Ziel des Drucks in Punkten je Zoll. Alles darunter sieht man auf Papier. */
export const DRUCK_DPI = 300

const MM_JE_ZOLL = 25.4

/**
 * Längste Kante in Millimetern, die diese Pixelzahl bei der Ziel-Auflösung
 * noch trägt.
 *
 * @returns 0 für unbrauchbare Eingaben. Null Millimeter heisst "kein Platz
 *   passt", und genau so soll ein fehlender Wert wirken - nicht wie ein Bild,
 *   das beliebig gross darf.
 */
export function maxPrintMm(pixels: number, dpi: number = DRUCK_DPI): number {
  if (!Number.isFinite(pixels) || pixels <= 0) return 0
  if (!Number.isFinite(dpi) || dpi <= 0) return 0
  return (pixels / dpi) * MM_JE_ZOLL
}

/**
 * Die Fotoplätze des Layouts, gross nach klein.
 *
 * held ist die ganze Textspalte, halb die Hälfte davon in einer Zweierreihe,
 * viertel ein Drittel in einer Dreierreihe. Die Namen stehen genauso in den
 * Klassen von print.css.
 */
export type PhotoSlot = 'held' | 'halb' | 'viertel'

/**
 * Breite der Plätze in Millimetern.
 *
 * Muss zu print.css passen: dort steht dieselbe Aufteilung als Anteil der
 * Textspalte, hier als Zahl, weil eine dpi-Rechnung Millimeter braucht. Die
 * Textspalte einer A5-Seite ist bei 10 mm Seitenrand 128 mm breit; halb und
 * viertel sind abzüglich der Fugen gerechnet.
 */
export const SLOT_BREITE_MM: Record<PhotoSlot, number> = {
  held: 128,
  halb: 62,
  viertel: 40,
}

/** Die Plätze in absteigender Grösse. Reihenfolge ist Teil der Aussage. */
export const SLOT_REIHE: PhotoSlot[] = ['held', 'halb', 'viertel']

/**
 * Pixelzahl, mit der gerechnet wird, wenn die echte nicht bekannt ist.
 *
 * Die kleinere der beiden Grössen, die im Bestand vorkommen: neue Bilder haben
 * 2400 Pixel längste Kante, ältere noch 1600. Wer nicht weiss, welches er in
 * der Hand hat, rechnet mit dem schlechteren.
 */
export const ANGENOMMENE_KANTE_PX = 1600

/** Trägt ein Bild dieser Pixelzahl den Platz in voller Auflösung? */
export function fitsSlot(
  slot: PhotoSlot,
  longestEdgePx: number = ANGENOMMENE_KANTE_PX,
  dpi: number = DRUCK_DPI,
): boolean {
  return maxPrintMm(longestEdgePx, dpi) >= SLOT_BREITE_MM[slot]
}

/**
 * Der gewünschte Platz, oder der nächstkleinere, den das Bild noch trägt.
 *
 * @returns null, wenn das Bild für keinen Platz genug Pixel hat. Dann gehört
 *   es nicht ins Buch - lieber ein Foto weniger als eines, das den Eindruck
 *   ruiniert.
 */
export function slotThatFits(
  desired: PhotoSlot,
  longestEdgePx: number = ANGENOMMENE_KANTE_PX,
  dpi: number = DRUCK_DPI,
): PhotoSlot | null {
  const ab = SLOT_REIHE.indexOf(desired)
  for (const slot of SLOT_REIHE.slice(ab < 0 ? 0 : ab)) {
    if (fitsSlot(slot, longestEdgePx, dpi)) return slot
  }
  return null
}

/**
 * Dateigrösse, ab der ein Foto als Titelbild in Frage kommt.
 *
 * Das Titelbild wird über die ganze Seite gezogen und ist das erste, was
 * jemand sieht. Eine sehr kleine Datei bedeutet bei gleicher Kantenlänge
 * wenige Details - ein flächiger Himmel, ein Bild aus einer fremden Quelle, ein
 * Bildschirmfoto. Als Beweis taugt die Dateigrösse nicht, als Ausschluss
 * schon.
 */
export const TITELBILD_MIN_BYTES = 120 * 1024

/**
 * Wählt das Foto für die Titelseite: das detailreichste, das da ist.
 *
 * Pixelmasse liegen nicht vor (siehe oben), also entscheidet die Dateigrösse.
 * Bei gleicher Grösse gewinnt das ältere Bild, danach die kleinere ID - die
 * Titelseite soll bei jedem Druck dieselbe sein und nicht davon abhängen, in
 * welcher Reihenfolge der Bestand gelesen wurde.
 *
 * @returns null, wenn kein Bild die Schwelle erreicht. Dann bleibt die
 *   Titelseite typografisch, und das ist kein Notbehelf: eine Seite aus Schrift
 *   ist besser als ein aufgeblasenes Bild.
 */
export function titlePhoto(
  photos: readonly PhotoMeta[],
  minBytes: number = TITELBILD_MIN_BYTES,
): PhotoMeta | null {
  let beste: PhotoMeta | null = null
  for (const foto of photos) {
    if (!Number.isFinite(foto.size) || foto.size < minBytes) continue
    if (!beste || besser(foto, beste)) beste = foto
  }
  return beste
}

function besser(a: PhotoMeta, b: PhotoMeta): boolean {
  if (a.size !== b.size) return a.size > b.size
  if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt
  return a.id < b.id
}
